import { APP_CONFIG } from '@/config/app';
import type {
  CallRecordingsResponse,
  CallTranscriptsResponse,
} from '@/types/calls';
import { callsService, userService } from '@/services';
import type { Call } from '@/types/calls';
import { isEavesdropHistoryCall } from '@/constants/telephony';
import { getCallHistoryStatusPillVariant } from '@/utils/callHistoryDisplayUtils';
import { getCallDurationSeconds } from '@/utils/callHistoryDurationUtils';

export type DashboardHistoryTab = 'nominals' | 'users';

export function isVisibleCallHistoryRow(call: Call): boolean {
  if (isEavesdropHistoryCall(call)) {
    return false;
  }
  if (!call.caller_id_number && !call.destination_number) {
    return false;
  }
  if (
    getCallHistoryStatusPillVariant(call) === 'unknown' &&
    getCallDurationSeconds(call) <= 0
  ) {
    return false;
  }
  return Boolean(
    call.end_stamp || call.hangup_cause || (call.duration ?? 0) > 0
  );
}

/**
 * Loads visible call-history rows until the dashboard list is full or the API has no more pages.
 * A single page often yields fewer rows after isVisibleCallHistoryRow filters eavesdrop CDRs.
 */
export async function fetchVisibleDashboardCallHistory(
  tab: DashboardHistoryTab,
  targetCount: number
): Promise<Call[]> {
  const visible: Call[] = [];
  let page = 1;
  const pageSize = Math.max(targetCount, 20);

  while (visible.length < targetCount) {
    const response =
      tab === 'nominals'
        ? await callsService.getNominalCallHistory({
            page,
            page_size: pageSize,
          })
        : await userService.getUserCallHistory({ page, page_size: pageSize });

    for (const call of response.results) {
      if (!isVisibleCallHistoryRow(call)) {
        continue;
      }
      visible.push(call);
      if (visible.length >= targetCount) {
        return visible;
      }
    }

    if (!response.next || response.results.length === 0) {
      break;
    }
    page += 1;
  }

  return visible;
}

type CallMediaResult = {
  recordings: CallRecordingsResponse;
  transcripts: CallTranscriptsResponse;
};

function emptyRecordings(callUuid: string): CallRecordingsResponse {
  return { recordings: [], count: 0, call_uuid: callUuid };
}

function emptyTranscripts(callUuid: string): CallTranscriptsResponse {
  return {
    transcripts: [],
    count: 0,
    call_uuid: callUuid,
    final_only: true,
  };
}

async function loadCallMediaForEndpoint(
  callUuid: string,
  isUserCall: boolean
): Promise<CallMediaResult & { bothRejected: boolean }> {
  const [recordingsResult, transcriptsResult] = await Promise.allSettled([
    callsService.getCallRecordings(callUuid, isUserCall),
    callsService.getCallTranscripts(callUuid, true, isUserCall),
  ]);

  if (recordingsResult.status === 'rejected') {
    console.error('Error fetching recordings:', recordingsResult.reason);
  }
  if (transcriptsResult.status === 'rejected') {
    console.error('Error fetching transcripts:', transcriptsResult.reason);
  }

  return {
    recordings:
      recordingsResult.status === 'fulfilled'
        ? recordingsResult.value
        : emptyRecordings(callUuid),
    transcripts:
      transcriptsResult.status === 'fulfilled'
        ? transcriptsResult.value
        : emptyTranscripts(callUuid),
    bothRejected:
      recordingsResult.status === 'rejected' &&
      transcriptsResult.status === 'rejected',
  };
}

/**
 * Prefer the endpoint for the list the call came from (nominals vs users tab).
 * If both requests 404, retry the other endpoint — same fallback idea as keywords modal.
 */
export async function loadCallRecordingsAndTranscriptsWithFallback(
  callUuid: string,
  preferredIsUserCall: boolean
): Promise<CallMediaResult> {
  const primary = await loadCallMediaForEndpoint(callUuid, preferredIsUserCall);
  if (!primary.bothRejected) {
    return {
      recordings: primary.recordings,
      transcripts: primary.transcripts,
    };
  }

  const fallback = await loadCallMediaForEndpoint(
    callUuid,
    !preferredIsUserCall
  );
  return {
    recordings: fallback.recordings,
    transcripts: fallback.transcripts,
  };
}

export function recordingFileRequestPath(fileUrl: string): string {
  const apiBase = APP_CONFIG.API_BASE_URL.replace(/\/$/, '');
  let path = fileUrl;
  if (fileUrl.startsWith(apiBase)) {
    path = fileUrl.slice(apiBase.length) || '/';
  } else if (fileUrl.startsWith('/api/')) {
    path = fileUrl.slice('/api'.length);
  } else {
    try {
      const parsed = new URL(fileUrl);
      path = parsed.pathname.startsWith('/api/')
        ? `${parsed.pathname.slice('/api'.length)}${parsed.search}`
        : `${parsed.pathname}${parsed.search}`;
    } catch {
      path = fileUrl;
    }
  }

  const marker = '/media/recordings/';
  const markerIndex = path.indexOf(marker);
  if (markerIndex === -1) {
    return path;
  }
  const filename = path.slice(markerIndex + marker.length).split(/[?#]/)[0];
  return `/recordings/${filename}`;
}

/** The audio element cannot send the dashboard JWT, so recording bytes are fetched through the API. */
export function recordingUrlNeedsAuthenticatedFetch(fileUrl: string): boolean {
  return recordingFileRequestPath(fileUrl).startsWith('/recordings/');
}

export function isRecordingVideo(
  recording: { is_video?: boolean },
  callIsVideo: boolean
): boolean {
  if (recording.is_video) {
    return true;
  }
  return callIsVideo;
}
